import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import { FiEdit, FiTrash2, FiPlus, FiX, FiCheckCircle, FiSave, FiMapPin, FiBriefcase } from 'react-icons/fi';

export default function ProjectManager() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProject, setEditingProject] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [imageFiles, setImageFiles] = useState([null, null, null, null, null]);

    const fetchProjects = () => {
        fetch(`${API_BASE}/admin/projects.php`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setProjects(data.projects || []);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleEdit = (proj) => {
        fetch(`${API_BASE}/admin/projects.php?id=${proj.id}`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') {
                    setEditingProject(data.project);
                    setImageFiles([null, null, null, null, null]); // Reset file inputs when editing
                    setShowModal(true);
                }
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this project?')) return;
        fetch(`${API_BASE}/admin/projects.php?id=${id}`, { method: 'DELETE' })
            .then(r => r.json())
            .then(() => fetchProjects());
    };

    const handleSave = (e) => {
        e.preventDefault();

        const formData = new FormData();
        Object.keys(editingProject).forEach(key => {
            if (key !== 'gallery') { // Don't send gallery array back as is
                formData.append(key, editingProject[key] || '');
            }
        });

        imageFiles.forEach((file, index) => {
            if (file) {
                formData.append(`image_file${index}`, file);
            }
        });

        fetch(`${API_BASE}/admin/projects.php`, {
            method: 'POST',
            body: formData
        })
            .then(r => r.json())
            .then(() => {
                setShowModal(false);
                setImageFiles([null, null, null, null, null]);
                fetchProjects();
            });
    };

    const handleFileChange = (index, file) => {
        const newFiles = [...imageFiles];
        newFiles[index] = file;
        setImageFiles(newFiles);
    };

    if (loading) return <div className="p-12 text-center text-slate-400">Syncing portfolio...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-slate-500 text-sm font-bold uppercase tracking-widest">Master Portfolio</h3>
                <button
                    onClick={() => {
                        setEditingProject({ name: '', location: '', city: '', project_type: '', description: '', image_url: '', completion_year: new Date().getFullYear() });
                        setImageFiles([null, null, null, null, null]);
                        setShowModal(true);
                    }}
                    className="btn-yellow flex items-center gap-2 px-6 py-2.5 text-sm"
                >
                    <FiPlus /> Add Project
                </button>
            </div>

            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-x divide-y">
                    {projects.length === 0 ? (
                        <div className="col-span-full p-20 text-center text-slate-400 font-medium">No projects added yet.</div>
                    ) : projects.map((p) => (
                        <div key={p.id} className="p-6 hover:bg-slate-50 transition-colors group relative">
                            <div className="aspect-video rounded-xl bg-slate-100 overflow-hidden mb-4 border mb-4">
                                <img
                                    src={p.image_url || 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80'}
                                    alt={p.name}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                            </div>
                            <div className="space-y-1">
                                <h4 className="font-bold text-slate-900 line-clamp-1">{p.name}</h4>
                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <FiMapPin size={12} className="text-[#2E63E8]" /> {p.location}, {p.city}
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <FiBriefcase size={12} className="text-[#2E63E8]" /> {p.project_type}
                                </div>
                            </div>

                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEdit(p)} className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-sm text-slate-600 hover:text-[#2E63E8] border transition-colors"><FiEdit /></button>
                                <button onClick={() => handleDelete(p.id)} className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-sm text-slate-600 hover:text-red-500 border transition-colors"><FiTrash2 /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Project Editor Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in duration-200">
                        <div className="p-8 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-slate-800">{editingProject?.id ? 'Edit' : 'New'} Project</h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors font-bold"><FiX size={24} /></button>
                        </div>

                        <form onSubmit={handleSave} className="p-8 space-y-5">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Project Name</label>
                                <input
                                    type="text"
                                    required
                                    value={editingProject.name}
                                    onChange={e => setEditingProject({ ...editingProject, name: e.target.value })}
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] outline-none bg-slate-50/50"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Location (Building/Area)</label>
                                    <input type="text" value={editingProject.location} onChange={e => setEditingProject({ ...editingProject, location: e.target.value })} className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] outline-none bg-slate-50/50" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">City</label>
                                    <input
                                        type="text"
                                        required
                                        value={editingProject.city}
                                        onChange={e => setEditingProject({ ...editingProject, city: e.target.value })}
                                        className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-[#2E63E8] outline-none bg-slate-50/50"
                                        placeholder="e.g. Hyderabad"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Project Type</label>
                                    <input type="text" placeholder="e.g. 3BHK Apartment" value={editingProject.project_type} onChange={e => setEditingProject({ ...editingProject, project_type: e.target.value })} className="w-full border p-3 rounded-xl outline-none bg-slate-50/50" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Completion Year</label>
                                    <input type="number" value={editingProject.completion_year} onChange={e => setEditingProject({ ...editingProject, completion_year: e.target.value })} className="w-full border p-3 rounded-xl outline-none bg-slate-50/50" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description</label>
                                <textarea
                                    rows="4"
                                    placeholder="Enter project details..."
                                    value={editingProject.description}
                                    onChange={e => setEditingProject({ ...editingProject, description: e.target.value })}
                                    className="w-full border p-3 rounded-xl outline-none bg-slate-50/50"
                                ></textarea>
                            </div>

                            <div className="pt-4">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-3">Project Images (Up to 5)</label>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                    {[0, 1, 2, 3, 4].map((idx) => (
                                        <div key={idx} className="relative aspect-square border-2 border-dashed rounded-xl overflow-hidden group bg-slate-50 hover:bg-slate-100 transition-colors">
                                            {imageFiles[idx] || editingProject?.gallery?.[idx] ? (
                                                <img 
                                                    src={imageFiles[idx] ? URL.createObjectURL(imageFiles[idx]) : editingProject.gallery[idx].image_url} 
                                                    className="w-full h-full object-cover" 
                                                />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1">
                                                    <FiPlus size={20} />
                                                    <span className="text-[10px] font-bold">{idx + 1}</span>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={e => handleFileChange(idx, e.target.files[0])}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-2 italic">Click slots to upload. First image is the main cover.</p>
                            </div>

                            <div className="pt-8 text-right">
                                <button type="submit" className="bg-[#2E63E8] hover:bg-[#1C4ED8] text-white font-bold py-4 px-10 rounded-xl transition-all flex items-center gap-2 ml-auto shadow-lg shadow-blue-200">
                                    <FiSave /> {editingProject?.id ? 'Update Project' : 'Publish Project'}
                                </button>
                            </div>
                        </form>

                    </div>
                </div>
            )}
        </div>
    );
}

